import React, { useState, useEffect } from 'react';
import SelectField from '../components/SelectField';
import { FaEraser, FaPlus } from 'react-icons/fa';
import LoadingState from '../components/LoadingState';
import { useNavigate } from 'react-router-dom';
import { getSk } from '../api/api';
import ConfirmationModal from '../components/ConfirmationModal';

const QaEditor = ({ setAlert }) => {
  const navigate = useNavigate();
  const [qaScorecards, setQaScorecards] = useState([]);
  const [selectedScorecard, setSelectedScorecard] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingScorecards, setLoadingScorecards] = useState(false);
  const [error, setError] = useState(null);
  const [scores, setScores] = useState({});
  const [, setCurrentCallId] = useState('CALL_001');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [modalInfo, setModalInfo] = useState({ open: false, title: '', message: '' });

  // Fetch available campaigns on mount
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const result = await getSk('campaign');
        const itms = result.Items || [];
        const campaignOptions = itms
          .map(i => i.SK?.S)
          .filter(Boolean)
          .map(c => ({ label: c, value: c }));
        setCampaigns(campaignOptions);
      } catch (err) {
        console.error('Error loading campaigns:', err);
        setError('Failed to load campaigns.');
      } finally {
        setLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, []);

  // Load scorecards when a campaign is selected
  useEffect(() => {
    if (!selectedCampaign) {
      setQaScorecards([]);
      setSelectedScorecard(null);
      return;
    }

    const fetchScorecards = async () => {
      setLoadingScorecards(true);
      try {
        const res = await getSk(selectedCampaign);
        const scorecards = res.Items || [];

        const grouped = {};
        scorecards.forEach(item => {
          const scorecardName = item.template?.S;
          if (!scorecardName) return;
          const version = item.version?.S || '1';
          if (!grouped[scorecardName]) {
            grouped[scorecardName] = {};
          }
          if (!grouped[scorecardName][version]) {
            grouped[scorecardName][version] = [];
          }
          grouped[scorecardName][version].push({
            name: item.SK?.S,
            description: item.description?.S || '',
            type: item.type?.S || 'boolean',
            weight: parseInt(item.weight?.S || 0)
          });
        });

        const scorecardArr = Object.keys(grouped).map(name => ({
          name,
          versions: grouped[name]
        }));
        setQaScorecards(scorecardArr);
        if (scorecardArr.length > 0) {
          const latestVersion = Object.keys(scorecardArr[0].versions).sort((a,b) => parseInt(a)-parseInt(b)).pop();
          setSelectedScorecard(scorecardArr[0]);
          setSelectedVersion(latestVersion);
        } else {
          setSelectedScorecard(null);
          setSelectedVersion('');
        }
      } catch (err) {
        console.error('Error fetching scorecards:', err);
        setError('Failed to load QA scorecards.');
        setQaScorecards([]);
        setSelectedScorecard(null);
      } finally {
        setLoadingScorecards(false);
      }
    };

    fetchScorecards();
  }, [selectedCampaign]);

  const handleScoreChange = (fieldName, value) => {
    setScores(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const calculateTotalScore = () => {
    const fields = selectedScorecard?.versions?.[selectedVersion] || [];
    if (fields.length === 0) return 0;

    let totalScore = 0;
    let totalWeight = 0;

    fields.forEach(field => {
      const score = scores[field.name];
      if (score !== undefined && score !== '') {
        let normalizedScore = 0;
        
        if (field.type === 'boolean') {
          normalizedScore = score === 'true' || score === true ? 100 : 0;
        } else if (field.type === 'score') {
          normalizedScore = parseInt(score) || 0;
        } else {
          // rating (1-5): scale to 0-100 so it weights like the other types
          normalizedScore = (parseInt(score) || 0) * 20;
        }

        totalScore += (normalizedScore * field.weight) / 100;
        totalWeight += field.weight;
      }
    });
    
    return totalWeight > 0 ? Math.round(totalScore) : 0;
  };

  const handleSubmit = () => {
    const totalScore = calculateTotalScore();

    setAlert({
      variant: 'success',
      heading: 'QA Assessment Saved',
      text: `Assessment completed with score: ${totalScore}/100`
    });

    setModalInfo({
      open: true,
      title: 'Assessment Saved',
      message: `Assessment completed with score: ${totalScore}/100`
    });

    // Reset form
    setScores({});
  };

  if (loadingCampaigns) {
    return (
      <LoadingState variant="form" label="Loading campaigns" />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">QA Scorecard</h1>
          <p className="text-gray-600 mt-1">
            Evaluate call quality using standardized assessment criteria
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/scorecards?new=1')}
            className="btn-outline"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Scorecard
          </button>
          <button
            onClick={() => navigate('/scorecards')}
            className="btn-outline"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a1 1 0 001 1h11a2 2 0 002-2v-5M18.5 2.5l3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Manage Scorecards
          </button>
        </div>
      </div>
      {error && (
        <div className="text-red-600 text-sm mt-2">{error}</div>
      )}

      {/* Scorecard Selection */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Campaign
            </label>
            {loadingCampaigns ? (
              <div className="text-gray-500">Loading campaigns...</div>
            ) : (
              <SelectField
                value={selectedCampaign || ''}
                onChange={(e) => {
                  setSelectedCampaign(e.target.value || null);
                  setScores({});
                }}
                className="select-field"
              >
                <option value="">Select campaign...</option>
                {campaigns.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </SelectField>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              QA Scorecard
            </label>
            {loadingScorecards ? (
              <div className="text-gray-500">Loading scorecards...</div>
            ) : (
              <SelectField
                value={selectedScorecard?.name || ''}
                onChange={(e) => {
                  const sc = qaScorecards.find(t => t.name === e.target.value);
                  setSelectedScorecard(sc || null);
                  setSelectedVersion('');
                  setScores({});
                }}
                className="select-field"
              >
                <option value="">Select scorecard...</option>
                {qaScorecards.map((sc) => (
                  <option key={sc.name} value={sc.name}>{sc.name}</option>
                ))}
              </SelectField>
            )}
          </div>
          {!loadingScorecards && selectedScorecard && (
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Version</label>
              <SelectField
                value={selectedVersion}
                onChange={(e) => {
                  setSelectedVersion(e.target.value);
                  setScores({});
                }}
                className="select-field"
              >
                {Object.keys(selectedScorecard.versions)
                  .sort((a,b)=>parseInt(a)-parseInt(b))
                  .map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
              </SelectField>
            </div>
          )}
        </div>
      </div>

      {/* QA Assessment Form */}
      {selectedScorecard && selectedVersion && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Assessment: {selectedScorecard.name} v{selectedVersion}
            </h2>
            <div className="text-lg font-bold text-cyan-600">
              Score: {calculateTotalScore()}/100
            </div>
          </div>

          <div className="space-y-6">
            {(selectedScorecard.versions?.[selectedVersion] || []).map((field, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {field.name}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {field.description}
                    </p>
                  </div>
                  <div className="text-sm text-gray-500 ml-4">
                    Weight: {field.weight}%
                  </div>
                </div>

                <div className="mt-4">
                  {field.type === 'boolean' ? (
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={field.name}
                          value="true"
                          checked={scores[field.name] === 'true'}
                          onChange={(e) => handleScoreChange(field.name, e.target.value)}
                          className="rounded-lg border-gray-200 text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={field.name}
                          value="false"
                          checked={scores[field.name] === 'false'}
                          onChange={(e) => handleScoreChange(field.name, e.target.value)}
                          className="rounded-lg border-gray-200 text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">No</span>
                      </label>
                    </div>
                  ) : field.type === 'score' ? (
                    <div className="flex items-center space-x-4">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scores[field.name] || 0}
                        onChange={(e) => handleScoreChange(field.name, e.target.value)}
                        className="flex-1"
                      />
                      <div className="w-20">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={scores[field.name] || ''}
                          onChange={(e) => handleScoreChange(field.name, e.target.value)}
                          className="input-field text-center"
                        />
                      </div>
                    </div>
                  ) : (
                    <SelectField
                      value={scores[field.name] || ''}
                      onChange={(e) => handleScoreChange(field.name, e.target.value)}
                      className="select-field"
                    >
                      <option value="">Select rating...</option>
                      <option value="1">1 - Poor</option>
                      <option value="2">2 - Below Average</option>
                      <option value="3">3 - Average</option>
                      <option value="4">4 - Good</option>
                      <option value="5">5 - Excellent</option>
                    </SelectField>
                  )}
                </div>

                {/* Score preview for this field */}
                <div className="mt-3 text-sm text-gray-500">
                  Field Score: {(() => {
                    const score = scores[field.name];
                    if (score === undefined || score === '') return 'Not assessed';
                    
                    let normalizedScore = 0;
                    if (field.type === 'boolean') {
                      normalizedScore = score === 'true' ? 100 : 0;
                    } else if (field.type === 'score') {
                      normalizedScore = parseInt(score) || 0;
                    } else {
                      // rating (1-5): scale to 0-100, matching calculateTotalScore
                      normalizedScore = (parseInt(score) || 0) * 20;
                    }
                    
                    const weightedScore = (normalizedScore * field.weight) / 100;
                    return `${normalizedScore}/100 (${weightedScore.toFixed(1)} weighted points)`;
                  })()}
                </div>
              </div>
            ))}
          </div>

          {/* Assessment Actions */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-6 mt-6 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Total Weight: {(selectedScorecard.versions?.[selectedVersion] || []).reduce((sum, field) => sum + field.weight, 0)}%
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setScores({})}
                className="btn-outline"
              >
                <FaEraser /> Clear All
              </button>
              
              <button
                onClick={handleSubmit}
                className="btn-primary"
                disabled={Object.keys(scores).length === 0}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save Assessment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No Scorecard Selected */}
      {!selectedScorecard && !loadingCampaigns && !loadingScorecards && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No QA scorecard available</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create a campaign with QA scorecards to start assessments.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/campaigns/new')}
              className="btn-primary"
            >
              <FaPlus /> Create Campaign
            </button>
          </div>
        </div>
      )}
      <ConfirmationModal
        open={modalInfo.open}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setModalInfo({ ...modalInfo, open: false })}
      />
    </div>
  );
};

export default QaEditor;